import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { Box, Typography, CircularProgress } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { getTranslation } from './i18n';
import { initializeUISettings, setLanguage } from './store/actions/uiActions';
import MapPage from './components/pages/MapPage';
import ContactPage from './components/pages/ContactPage';
import PrivacyPage from './components/pages/PrivacyPage';
import TermsPage from './components/pages/TermsPage';
import SensorInfrastructurePage from './components/pages/SensorInfrastructurePage';
import GeofencePage from './components/pages/GeofencePage';
import ModernHeader from './components/layout/ModernHeader';
import Footer from './components/layout/Footer';
import LandingPage from './components/landing/LandingPage';
import './App.css';

function App() {
  const location = useLocation();
  const dispatch = useDispatch();
  const { theme, language } = useSelector((state) => state.ui);
  const translations = getTranslation(language);
  const [initialized, setInitialized] = useState(false);

  // Initialize UI settings on app load
  useEffect(() => {
    // Initialize UI settings (including language from localStorage)
    dispatch(initializeUISettings());
    setInitialized(true);
  }, [dispatch]);

  // Create theme with the main color from the Flutter app
  const muiTheme = createTheme({
    palette: {
      mode: 'light',
      primary: {
        main: '#1273D1', // Main color from Flutter app
      },
      secondary: {
        main: '#0E58A0', // Secondary color from Flutter app
      },
      background: {
        default: '#F5F5F5',
        paper: '#FFFFFF',
      },
      text: {
        primary: '#212121',
        secondary: '#757575',
      },
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    },
    components: {
      MuiAppBar: {
        defaultProps: {
          elevation: 0,
        },
      },
    },
  });

  // Show loading indicator until UI settings are initialized
  if (!initialized) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      <div className="app">
        <ModernHeader />
        <main className="main-content">
          <Routes>
            {/* Geofence share page — must come before /:lang */}
            <Route path="/g/:code" element={<GeofencePage />} />

            {/* Language-specific landing pages */}
            <Route path="/:lang" element={<LandingPage />} />

            {/* Redirect root to user's preferred language landing page */}
            <Route path="/" element={<Navigate to={`/${language}`} replace />} />

            {/* Map routes */}
            <Route path="/map" element={<MapPage />} />
            <Route path="/map/:eventId" element={<MapPage />} />

            {/* Contact, Privacy, and Terms pages */}
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/sensor-infrastructure" element={<SensorInfrastructurePage />} />
          </Routes>
        </main>
        {/* Footer is outside of scrollable content but still within the flex container */}
        {!location.pathname.startsWith('/g/') && <Footer />}
      </div>
    </ThemeProvider>
  );
}

export default App;
